/*
    Created by Phat Pham
    phat@launchdeck.org
*/

import React, { useImperativeHandle, useState } from 'react';
import {
    View,
    Text,
    Modal,
    Image,
    ScrollView,
    useWindowDimensions
} from 'react-native-web';
import PropTypes from 'prop-types';

// Custom Components
import { Button, NavBar } from '../components';

// Configs
import GLOBAL from '../configs/config_global';
import DATA from '../configs/config_data';

export default function ServiceAgreementModal(props, ref) {
    const { width } = useWindowDimensions();
    // region Props
    const {
        truckName,
        onAccept,
        onDecline,
        readOnly,
        useView,
        declineText,
        style
    } = props;
    // endregion

    // region Local State
    const [modalShown, setModalShown] = useState(false);
    // endregion

    // region Function - Show/Hide Modal
    const showModal = () => setModalShown(true);
    const hideModal = () => setModalShown(false);
    // endregion

    // region Expose Methods
    useImperativeHandle(ref, () => ({ showModal, hideModal }), []);
    // endregion

    // region MAIN RENDER
    const splitTextFirst = DATA.serviceAgreement?.replace('[INSERT NAME OF FOOD TRUCK]', truckName).split(`Hollowell Partners, LLC, dba “Truckster”`);
    const splitTextSecond = splitTextFirst[1].split(truckName);
    const MyComp = useView ? View : Modal;
    return(
        <MyComp
            visible={modalShown}
            onDismiss={hideModal}
            onRequestClose={hideModal}
            animationType={'slide'}
            transparent={true}
            style={useView ? [{ flex: 1, width: '100%', paddingHorizontal: '20%' }].concat(style) : { margin: 0 }}>
            {/* Nav Bar */}
            {
                !useView &&
                <NavBar
                    title={''}
                    horizontalSpacing={16}
                    leftButtonOnPress={() => {
                        !!onDecline && onDecline();
                        hideModal();
                    }}
                    hideBorder={true}
                />
            }

            {/* Content */}
            <View style={{
                flex: 1, width: '100%', backgroundColor: 'white', alignItems: 'center',
                paddingTop: useView ? 32 : 0, paddingBottom: ((readOnly || useView) ? 0 : 24)
            }}>
                {/* Header */}
                <Image
                    source={require('../images/logo_truckster.png')}
                    style={{ width: 80, height: 45 }}
                    resizeMode={'contain'}
                />

                {/* Title */}
                <Text style={{ fontWeight: '900', fontSize: 24, letterSpacing: -0.4, color: GLOBAL.COLOR.BASIC.BLACK_TWO, lineHeight: 32, textAlign: 'center', marginVertical: 16 }}>
                    Truckster Service Agreement
                </Text>

                {/* Content */}
                <View style={{ flex: 1, width: '100%' }}>
                    {/* Text */}
                    <ScrollView
                        key={'serviceAgreementScrollView'}
                        style={{ flex: 1 }}
                        contentContainerStyle={{ padding: 24 }}>
                        {/* Main Texts */}
                        <Text style={{ fontSize: 16, color: GLOBAL.COLOR.BASIC.BLACK_TWO, lineHeight: 24, marginBottom: 32 }}>
                            {splitTextFirst[0]}
                            <Text style={{ fontWeight: 'bold', fontSize: 16, color: GLOBAL.COLOR.BASIC.UI_BLUE }}>
                                Hollowell Partners, LLC, dba “Truckster”
                            </Text>
                            {splitTextSecond[0]}
                            <Text style={{ fontWeight: 'bold', fontSize: 16, color: GLOBAL.COLOR.BASIC.UI_BLUE }}>
                                {truckName}
                            </Text>
                            {splitTextSecond[1]}
                        </Text>

                        {/* Exhibit Image */}
                        <Text style={{ fontSize: 18, color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: 'bold', textDecorationLine: 'underline', alignSelf: 'center', marginBottom: 16 }}>
                            Exhibit A
                        </Text>

                        <Image
                            source={require('../images/agreement_image1.png')}
                            style={{ width: '100%', height: width * 0.6 * 1.27 }}
                            resizeMode={'contain'}
                        />

                        {/* Final Texts */}
                        {
                            !readOnly &&
                            <Text style={{ fontSize: 16, color: GLOBAL.COLOR.BASIC.BLACK_TWO, lineHeight: 24, marginTop: 32 }}>
                                By selecting "Agree", Customer intends and agrees to be bound by the terms of this Agreement.
                            </Text>
                        }
                    </ScrollView>

                    {/* Gradient */}
                    <Image
                        source={require('../images/image_gradient_down.png')}
                        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 20 }}
                    />

                    <Image
                        source={require('../images/image_gradient.png')}
                        style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 20 }}
                    />
                </View>

                <View style={{ width: '100%', alignSelf: 'center', flexDirection: 'row', alignItems: 'center', paddingHorizontal: 24, paddingTop: readOnly ? 0 : 16, paddingBottom: 32 }}>
                    {
                        !readOnly &&
                        <Button
                            onPress={onDecline}
                            style={{ marginRight: 4, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, flex: 1 }}
                            text={declineText}
                            textColor={'black'}
                            textStyle={{ letterSpacing: 0.5, fontWeight: '500' }}
                        />
                    }

                    {
                        !readOnly &&
                        <Button
                            onPress={() => {
                                hideModal();
                                !!onAccept && onAccept();
                            }}
                            style={{ marginLeft: 4, flex: 1 }}
                            text={'Agree'}
                            textColor={'white'}
                            textStyle={{ letterSpacing: 0.5, fontWeight: '500' }}
                        />
                    }

                    {
                        readOnly &&
                        <Button
                            onPress={onAccept ? (() => onAccept()) : (() => hideModal())}
                            style={{ flex: 1 }}
                            text={'Done'}
                            textColor={'white'}
                            textStyle={{ letterSpacing: 0.5, fontWeight: '600' }}
                        />
                    }
                </View>
            </View>
        </MyComp>
    );
    // endregion
}

ServiceAgreementModal = React.forwardRef(ServiceAgreementModal);

// region Props
ServiceAgreementModal.propTypes = {
    truckName: PropTypes.string,
    onAccept: PropTypes.func,
    onDecline: PropTypes.func,
    readOnly: PropTypes.bool,
    useView: PropTypes.bool,
    declineText: PropTypes.string
};

ServiceAgreementModal.defaultProps = {
    truckName: 'Your Food Truck',
    onAccept: null,
    onDecline: () => {},
    readOnly: false,
    useView: false,
    declineText: 'Decline'
};
// endregion
